/**
 * Web Push utilities: register service worker + subscribe/unsubscribe +
 * best-effort Periodic Background Sync registration.
 */
import { http } from "./api";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
};

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const registerServiceWorker = async () => {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
};

export const getCurrentPushSubscription = async () => {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

const tryRegisterPeriodicSync = async (reg) => {
  try {
    if (!("periodicSync" in reg)) return;
    // @ts-ignore newer permission name
    const status = await navigator.permissions.query({
      name: "periodic-background-sync",
    });
    if (status.state !== "granted") return;
    await reg.periodicSync.register("mfsmai-poll-alerts", {
      minInterval: 12 * 60 * 60 * 1000,
    });
  } catch {
    // ignore — periodic sync is best-effort
  }
};

export const subscribeToPush = async () => {
  const reg = await registerServiceWorker();
  const { data: keyInfo } = await http.get("/push/public-key");
  if (!keyInfo.enabled || !keyInfo.publicKey) {
    throw new Error("Web Push not configured on server");
  }
  if (Notification.permission !== "granted") {
    const p = await Notification.requestPermission();
    if (p !== "granted") throw new Error("Notification permission denied");
  }

  let existing = await reg.pushManager.getSubscription();
  if (existing) {
    const sameKey =
      existing.options &&
      existing.options.applicationServerKey &&
      new Uint8Array(existing.options.applicationServerKey).join(",") ===
        urlBase64ToUint8Array(keyInfo.publicKey).join(",");
    if (!sameKey) {
      try { await existing.unsubscribe(); } catch {}
      existing = null;
    }
  }
  const subscription =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyInfo.publicKey),
    }));

  await http.post("/push/subscribe", subscription.toJSON());
  await tryRegisterPeriodicSync(reg);
  return subscription;
};

export const unsubscribeFromPush = async () => {
  const sub = await getCurrentPushSubscription();
  if (!sub) return false;
  try {
    await http.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch {}
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg && "periodicSync" in reg) {
      try { await reg.periodicSync.unregister("mfsmai-poll-alerts"); } catch {}
    }
  } catch {}
  try {
    await sub.unsubscribe();
  } catch {}
  return true;
};
